import { DateTime } from 'luxon';
import { DayTaskStatusCache, DayTaskStatuses } from '@cf-shared/data-model/day-task-status-cache';

/**
 * The one way to read, write or remove a day inside the day-task-status cache
 * document (df-827a7f3bf41e). Every stored day must carry a `computedOn` stamp, and
 * `putDay` is the only thing that can store one — `statusesByDate` is `readonly` on
 * the shared interface, so a direct write from anywhere else is a compile error. The
 * single cast that opens it is `mutableStatusesByDate` below.
 *
 * What the stamp is for, and why it was worth making structural, is in
 * `design-docs/sync-integrity.md` under the #4694 bullet.
 *
 * NO ANGULAR DEPENDENCIES, deliberately. `DayTaskStatusService` cannot be
 * instantiated under node, which is why so much of that class is pinned by source
 * tripwires. This class can be, so the stamp is pinned by a real test that stores a
 * day and reads it back.
 *
 * LIFETIME. Wraps one already-loaded cache document and holds it for exactly as long
 * as the caller holds that document — construct it right after
 * `loadDayTaskStatusCacheAsync()` and let it go out of scope with the local variable.
 * It deliberately does NOT load, save or re-fetch anything, and it knows nothing
 * about the database. That keeps every `reactiveStatusGeneration` check in the
 * service reasoning about the same object it always did: a switch of user database
 * across an await is still the caller's problem, handled the same way as before.
 */
export class DayTaskStatusCacheDoc {
  constructor(private readonly doc: DayTaskStatusCache) {
    // if the document has no day map at all, give it one now
    //
    // A document written by an older app version, or one whose map was dropped, would
    // otherwise throw on the first write. Two of the three old write paths guarded
    // against this by hand and the third did not, so the guard living here also
    // removes that inconsistency.
    if (!this.doc.statusesByDate) {
      this.mutableStatusesByDate = {};
    }
  }

  /**
   * The day map, opened for writing.
   *
   * `statusesByDate` is `readonly` on `DayTaskStatusCache` so that nothing outside
   * this class can store a day. This one cast is what that readonly is protecting:
   * keep it here, and keep it the only one.
   */
  private set mutableStatusesByDate(map: { [date: string]: DayTaskStatuses }) {
    (this.doc as { statusesByDate: { [date: string]: DayTaskStatuses } }).statusesByDate = map;
  }

  private get mutableStatusesByDate(): { [date: string]: DayTaskStatuses } {
    return this.doc.statusesByDate as { [date: string]: DayTaskStatuses };
  }

  /**
   * The statuses stored for one day, or `undefined` when that day is not cached.
   */
  getDay(dateKey: string): DayTaskStatuses | undefined {
    return this.doc.statusesByDate[dateKey];
  }

  /**
   * Store one day's statuses, stamped with the day they were worked out on (#4694).
   *
   * `computedOnDay` is a PARAMETER and must stay one: pass the `today` your round
   * started with, never a fresh clock reading. A recompute that crosses local
   * midnight would otherwise be stamped with the new date, claiming it was worked out
   * after the day ended and suppressing the one re-check the stamp exists for.
   *
   * The stamp is applied here and never inside `buildDayStatusesForRange`, which is
   * shared with the in-memory display memo and must leave it unstamped. Both rules,
   * and what happens if either is broken, are in `design-docs/sync-integrity.md`
   * under the #4694 bullet.
   */
  putDay(dateKey: string, statuses: DayTaskStatuses, computedOnDay: DateTime): void {
    this.mutableStatusesByDate[dateKey] = { ...statuses, computedOn: computedOnDay.toISODate() };
  }

  /**
   * Remove one day from the cache, and report whether there was one to remove.
   *
   * The caller uses the answer to decide whether the document is worth saving — a
   * save that changed nothing still bumps a revision and travels to every other
   * device.
   */
  deleteDay(dateKey: string): boolean {
    if (this.doc.statusesByDate[dateKey] === undefined) {
      return false;
    }
    delete this.mutableStatusesByDate[dateKey];
    return true;
  }
}
